// src/pages/admin/Settings.tsx
//
// Application-level settings (per docs/operations/getting-started.md
// + docs/operations/iam-model.md). Four categories:
//   - Integrations: GLS, FileMaker, AWS, SMTP
//   - Identity:     Keycloak URL, realm, client id, allowed groups
//   - Sales:        default quota + commission + fiscal year start
//   - Audit:        retention days + PII masking
//
// Each entry is editable inline. Sensitive entries (API keys)
// show a masked "••••" toggle. Every change is appended to the
// settings audit log (also visible on this page).

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Settings as SettingsIcon,
  Eye,
  EyeOff,
  RotateCcw,
  Check,
  Shield,
  Lock,
  Power,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/cn";
import { useAuth } from "@/components/auth/AuthContext";
import { authHeaders } from "@/lib/api";
import {
  SETTINGS_CATEGORIES,
  type SettingsCategory,
  type SettingsEntry,
} from "@/lib/settings/types";
import { useSettings } from "@/lib/settings/useSettings";

/** i18n key per settings category (labels + descriptions). */
const CATEGORY_LABEL_KEY: Record<SettingsCategory, string> = {
  integrations: "admin.settings.categoryIntegrations",
  identity: "admin.settings.categoryIdentity",
  sales: "admin.settings.categorySales",
  audit: "admin.settings.categoryAudit",
  sync: "admin.settings.categorySync",
};

const CATEGORY_DESCRIPTION_KEY: Record<SettingsCategory, string> = {
  integrations: "admin.settings.descIntegrations",
  identity: "admin.settings.descIdentity",
  sales: "admin.settings.descSales",
  audit: "admin.settings.descAudit",
  sync: "admin.settings.descSync",
};

export function SettingsPage() {
  const { audit, byCategory, set, reset } = useSettings();
  const auth = useAuth();
  const { t } = useTranslation();
  const [activeCat, setActiveCat] = useState<SettingsCategory>("integrations");
  const [reveal, setReveal] = useState<Set<string>>(() => new Set());
  const isAdmin = auth.accountType === "admin";

  function toggleReveal(key: string) {
    setReveal((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <SettingsIcon
              className="h-5 w-5 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t("admin.settings.title")}
            </h1>
            {!isAdmin && (
              <Badge variant="warning" size="sm">
                {t("admin.settings.readOnlyBadge")}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.settings.intro")}
          </p>
        </div>
        {isAdmin && (
          <Button variant="ghost" size="sm" onClick={reset}>
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("admin.settings.resetDefaults")}
          </Button>
        )}
      </header>

      <TeddyPublicAdminControl isAdmin={isAdmin} />

      {/* Category tabs */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider text-fg-tertiary">
          {t("admin.settings.categoryLabel")}
        </span>
        {SETTINGS_CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setActiveCat(c.key)}
            aria-pressed={activeCat === c.key}
            className={cn(
              "rounded px-2 py-1 text-xs font-510 uppercase tracking-wider transition-colors",
              activeCat === c.key
                ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
            )}
          >
            {t(CATEGORY_LABEL_KEY[c.key])}
          </button>
        ))}
      </div>

      {/* Active category */}
      {SETTINGS_CATEGORIES.filter((c) => c.key === activeCat).map((c) => (
        <Card key={c.key}>
          <CardHeader>
            <CardTitle>{t(CATEGORY_LABEL_KEY[c.key])}</CardTitle>
            <CardDescription>{t(CATEGORY_DESCRIPTION_KEY[c.key])}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {byCategory(c.key).map((entry) => (
              <SettingsRow
                key={entry.key}
                entry={entry}
                revealed={reveal.has(entry.key)}
                onToggleReveal={() => toggleReveal(entry.key)}
                onSave={(value) =>
                  set(entry.key, value, auth.user?.email ?? "anonymous")
                }
                readOnly={!isAdmin}
              />
            ))}
          </CardContent>
        </Card>
      ))}

      {/* Audit log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield
              className="h-4 w-4 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            {t("admin.settings.changeLog")}
          </CardTitle>
          <CardDescription>
            {t("admin.settings.changeLogDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {audit.length === 0 ? (
            <div className="p-5">
              <EmptyState
                title={t("admin.settings.noChanges")}
                description={t("admin.settings.noChangesDescription")}
              />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {audit.slice(0, 10).map((a) => (
                <li key={a.id} className="px-5 py-3 text-xs">
                  <div className="flex items-center gap-2">
                    <Badge variant="default" size="sm">
                      {a.action}
                    </Badge>
                    <span className="font-mono text-[10px] text-fg-quaternary">
                      {t("admin.settings.by")} {a.actor}
                    </span>
                    <span className="ml-auto text-[11px] text-fg-tertiary">
                      {new Date(a.ts).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-fg-secondary">{a.summary}</p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsRow({
  entry,
  revealed,
  onToggleReveal,
  onSave,
  readOnly,
}: {
  entry: SettingsEntry;
  revealed: boolean;
  onToggleReveal: () => void;
  onSave: (next: string) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(entry.value);
  const [editing, setEditing] = useState(false);
  const isDirty = draft !== entry.value;

  // Re-sync when the store updates the entry externally.
  useMemo(() => setDraft(entry.value), [entry.value]);

  const masked =
    entry.sensitive && !revealed && entry.value.length > 0
      ? "•".repeat(Math.min(20, Math.max(8, entry.value.length)))
      : entry.value;

  return (
    <div className="rounded-md border border-border bg-bg-canvas/40 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-fg-quaternary">
              {entry.key}
            </span>
            {entry.sensitive && (
              <Badge variant="default" size="sm">
                <Lock className="h-2.5 w-2.5" strokeWidth={2} />
                {t("admin.settings.entrySensitive")}
              </Badge>
            )}
            {entry.lastEditedAt && (
              <span className="text-[10px] text-fg-quaternary">
                {t("admin.settings.lastEdited")}{" "}
                {new Date(entry.lastEditedAt).toLocaleDateString()} {t("admin.settings.by")}{" "}
                {entry.lastEditedBy}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-fg-tertiary">
            {t(`admin.settingsDescriptions.${entry.key.replace(/\./g, "_")}`, entry.description)}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        {editing ? (
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            type={entry.sensitive && !revealed ? "password" : "text"}
            className="flex-1 font-mono text-xs"
            autoFocus
          />
        ) : (
          <code className="flex-1 truncate rounded border border-border bg-bg-elevated px-2 py-1 font-mono text-xs text-fg-secondary">
            {masked}
          </code>
        )}
        {entry.sensitive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleReveal}
            aria-label={revealed ? t("admin.settings.hideValue") : t("admin.settings.showValue")}
          >
            {revealed ? (
              <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />
            ) : (
              <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
            )}
          </Button>
        )}
        {readOnly ? null : editing ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setDraft(entry.value);
                setEditing(false);
              }}
            >
              {t("admin.settings.cancel")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                onSave(draft);
                setEditing(false);
              }}
              disabled={!isDirty || draft.trim().length === 0}
            >
              <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("admin.settings.save")}
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setEditing(true)}
          >
            {t("admin.settings.edit")}
          </Button>
        )}
      </div>
    </div>
  );
}


type TeddyPublicControlState = {
  requested_enabled: boolean;
  incident_hold: boolean;
  environment_allowed: boolean;
  public_backend_ready: boolean;
  effective_enabled: boolean;
  version: number;
  updated_at: string;
  updated_by: string;
  reason: string;
};

/** Server-authoritative M4 safety control; NEVER backed by useSettings/localStorage. */
function TeddyPublicAdminControl({
  isAdmin,
}: {
  isAdmin: boolean;
}) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<TeddyPublicControlState | null>(null);
  const [reason, setReason] = useState(t("admin.settings.defaultReason"));
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [controlError, setControlError] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await fetch("/api/admin/teddy/public-control", {
          credentials: "include",
          cache: "no-store",
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(t("admin.settings.readError", { status: res.status }));
        const data = (await res.json()) as TeddyPublicControlState;
        if (!cancelled) {
          setCurrent(data);
          setControlError("");
        }
      } catch (error) {
        if (!cancelled) {
          setCurrent(null); // never display stale ON status if the backend is unreachable
          setControlError(error instanceof Error ? error.message : t("admin.settings.controlUnavailable"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void refresh();
    const interval = window.setInterval(() => { void refresh(); }, 15_000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [isAdmin]);

  const changeState = async (enabled: boolean) => {
    if (!current || !isAdmin || updating) return;
    const acknowledged = enabled && current.incident_hold;
    if (acknowledged && !window.confirm(
      t("admin.settings.confirmReenable")
    )) return;
    if (!enabled && !window.confirm(t("admin.settings.confirmDisable"))) return;
    const explanation = reason.trim();
    if (explanation.length < 5) {
      setControlError(t("admin.settings.reasonTooShort"));
      return;
    }
    setUpdating(true);
    setControlError("");
    try {
      const res = await fetch("/api/admin/teddy/public-control", {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          // BFF mode: cookie session + CSRF header; bearer mode:
          // Authorization header. Both come from authHeaders().
          ...authHeaders(),
        },
        body: JSON.stringify({
          enabled,
          reason: explanation,
          expected_version: current.version,
          acknowledge_incident: acknowledged,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { detail?: { code?: string } | string };
        const code = typeof body.detail === "object" ? body.detail?.code : body.detail;
        throw new Error(code
          ? t("admin.settings.updateRejected", { status: res.status, code })
          : t("admin.settings.updateRejectedNoCode", { status: res.status }));
      }
      setCurrent((await res.json()) as TeddyPublicControlState);
    } catch (error) {
      setControlError(error instanceof Error ? error.message : t("admin.settings.updateFailed"));
    } finally {
      setUpdating(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Power className="h-4 w-4 text-benchmark-teal-bright" strokeWidth={1.5} />
          {t("admin.settings.teddyTitle")}
        </CardTitle>
        <CardDescription>
          {t("admin.settings.teddyDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? <p className="text-sm text-fg-tertiary">{t("admin.settings.readingStatus")}</p> : current ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant={current.effective_enabled ? "success" : "warning"} size="sm">
                {current.effective_enabled ? t("admin.settings.teddyOn") : t("admin.settings.teddyOff")}
              </Badge>
              {current.incident_hold && (
                <Badge variant="warning" size="sm">{t("admin.settings.incidentLock")}</Badge>
              )}
              {!current.environment_allowed && (
                <Badge variant="default" size="sm">{t("admin.settings.localDevOnly")}</Badge>
              )}
              {!current.public_backend_ready && (
                <Badge variant="default" size="sm">{t("admin.settings.publicBackendNotReady")}</Badge>
              )}
            </div>
            <p className="text-xs text-fg-tertiary">
              {t("admin.settings.requested")} {current.requested_enabled ? t("admin.settings.on") : t("admin.settings.off")} ·
              {t("admin.settings.lastChanged")} {current.updated_at ? new Date(current.updated_at).toLocaleString() : t("admin.settings.unknown")} ·
              {t("admin.settings.actor")} {current.updated_by} · {t("admin.settings.version")} {current.version}
            </p>
            <p className="text-xs text-fg-secondary">{current.reason}</p>
            <label htmlFor="teddy-public-change-reason" className="block text-xs text-fg-tertiary">
              {t("admin.settings.reasonLabel")}
            </label>
            <Input
              id="teddy-public-change-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={!isAdmin || updating}
              className="w-full"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => { void changeState(true); }}
                isLoading={updating}
                disabled={!isAdmin || updating || current.effective_enabled ||
                  !current.environment_allowed || !current.public_backend_ready}
              >
                <Power className="h-3.5 w-3.5" /> {t("admin.settings.enableTeddy")}
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => { void changeState(false); }}
                disabled={!isAdmin || updating || (!current.requested_enabled && !current.effective_enabled)}
              >
                {t("admin.settings.disableTeddy")}
              </Button>
            </div>
            {current.incident_hold && (
              <p className="text-xs text-fg-tertiary">
                {t("admin.settings.incidentNotice")}
              </p>
            )}
          </>
        ) : (
          <p className="text-xs text-fg-tertiary">
            {t("admin.settings.serverUnavailable")}
          </p>
        )}
        {controlError && <p role="alert" className="text-xs text-accent-rose">{controlError}</p>}
        <p className="text-xs text-fg-quaternary">
          {t("admin.settings.teddyFooter")}
        </p>
      </CardContent>
    </Card>
  );
}
